<template>
  <div class="vue-more-actions-wrapper">
    <div class="more-actions-menu" :style="{ visibility: filteredActionsRef?.length ? 'visible' : 'hidden' }">
      <ul class="more-actions-menu__list">
        <li
          data-test-id="more-actions-menu"
          v-for="(action, index) in filteredActionsRef"
          :key="index"
          :class="`action-item ${action.isDanger ? 'danger' : ''}`"
          @click="handleClickAction(action)"
        >
          <component :is="returnIconComponent(action.icon)" color="black" filled></component>
          <span class="action-item__text">
            <small v-if="hintOf(action)" class="action-item__hint">{{ t(hintOf(action)!) }}</small>
            <span>{{ t(action.label) }}</span>
          </span>
        </li>
      </ul>
    </div>
    <div class="more-actions-menu-backdrop"></div>
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed } from 'vue'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import type { V2SessionType } from '@/features/sessions/types'
import AddFileIcon from '@/features/ui/components/icons/add-file-icon.vue'
import CalendarIcon from '@/features/ui/components/icons/calendar-icon.vue'
import CancelIcon from '@/features/ui/components/icons/cancel-icon.vue'
import ChangeIcon from '@/features/ui/components/icons/change-icon.vue'
import ConversationIcon from '@/features/ui/components/icons/conversation-icon.vue'
import CopyIcon from '@/features/ui/components/icons/copy-icon.vue'
import DownloadIcon from '@/features/ui/components/icons/download-icon.vue'
import EditIcon from '@/features/ui/components/icons/edit-icon.vue'
import EmailIcon from '@/features/ui/components/icons/email-icon.vue'
import LogoutIcon from '@/features/ui/components/icons/logout-icon.vue'
import SettingsIcon from '@/features/ui/components/icons/settings-icon.vue'
import ShowIcon from '@/features/ui/components/icons/show-icon.vue'
import UserIcon from '@/features/ui/components/icons/user-icon.vue'
import useMoreActionsMenu from '@/features/ui/components/more-actions-menu/hooks/use-more-actions-menu.hook'
import type { ActionType } from '@/features/ui/components/more-actions-menu/store/more-actions.store.ts'
import type { ConfirmActionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

const { moreActionsRef } = useMoreActionsMenu()
const toast = useToast()
const { t } = useI18n()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)
const { programRolesRef } = useProgramRoles()

// possible group id if the action is related to a session
const groupIdRef = computed(() => {
  return (moreActionsRef.value?.attachedData as V2SessionType | undefined)?.mentoringGroupId
})
const { groupRolesRef } = useGroupRoles({ groupIdRef })

function hintOf(action: ActionType) {
  return action.hint?.(moreActionsRef.value?.attachedData)
}

function handleClickAction(action: ActionType) {
  const moreActions = moreActionsRef.value
  const toastValue = toast

  if (moreActions && toastValue) {
    const run = () => action.fn({ attachedData: moreActions.attachedData, toast: toastValue, t })

    // if action is dangerous (or asks for it), show confirm modal
    if (action.isDanger || action.requiresConfirm) {
      // Retourner la promesse : la modale de confirmation l'attend et affiche
      // l'erreur si l'action échoue. Sans ce retour, un refus de l'API
      // (session déjà annulée…) partait en unhandled rejection et
      // l'utilisateur ne voyait rien.
      modalsStore.updateModalAttachedData<ConfirmActionModalType>('confirm-action', {
        fn: run,
        title: action.confirmLabel,
        content: action.confirmContent,
        // Confirmation demandée sur une action non destructive : bouton principal
        type: action.isDanger ? 'danger' : 'default',
      })
      modalsStore.toggleModal('confirm-action', true)

      // else just call the action
    } else {
      Promise.resolve(run()).catch((e) => {
        console.error(e)
        toastValue.error(t('error.common'))
      })
    }
  }
}

// filter actions based on restrictions
const filteredActionsRef = computed(() => {
  return moreActionsRef.value?.actions.filter((action) => {
    // if no restrictions, allow action
    // if role restriction, check if account role is in the list
    // if session status restriction, check if session status is in the list

    if (action.restrictions?.custom) {
      return action.restrictions.custom(moreActionsRef.value?.attachedData, {
        programRoles: programRolesRef.value,
        groupRoles: groupRolesRef.value,
      })
    }

    if (
      action.restrictions?.programRoles &&
      programRolesRef.value &&
      !programRolesRef.value.some((r) => action.restrictions?.programRoles?.includes(r))
    ) {
      return false
    }

    if (
      action.restrictions?.groupRoles &&
      groupRolesRef.value &&
      !groupRolesRef.value.some((r) => action.restrictions?.groupRoles?.includes(r))
    ) {
      return false
    }
    if (
      action.restrictions?.sessionsStatus &&
      (moreActionsRef.value?.attachedData as V2SessionType | undefined)?.status &&
      !action.restrictions.sessionsStatus.includes((moreActionsRef.value?.attachedData as V2SessionType).status)
    ) {
      return false
    }
    if (
      action.restrictions?.menteeCanBookSession &&
      groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) &&
      !programRef.value?.settings.menteeCanBookSession
    ) {
      return false
    }
    return true
  })
})

function returnIconComponent(icon: ActionType['icon']): Component {
  switch (icon) {
    case 'logout':
      return LogoutIcon
    case 'settings':
      return SettingsIcon
    case 'cancel':
      return CancelIcon
    case 'edit':
      return EditIcon
    case 'copy':
      return CopyIcon
    case 'show':
      return ShowIcon
    case 'conversation':
      return ConversationIcon
    case 'download':
      return DownloadIcon
    case 'add-file':
      return AddFileIcon
    case 'email':
      return EmailIcon
    case 'change':
      return ChangeIcon
    case 'calendar':
      return CalendarIcon
    case 'user':
      return UserIcon

    default:
      return ShowIcon
  }
}
</script>

<style lang="scss">
@import './more-actions-menu.scss';
</style>
